import { useState } from 'react';
import type { Car, RdwLookup } from '@fleet/shared';
import { euro, fmtDate } from '@fleet/shared';
import { Button, Input } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { ApiError } from '../../lib/apiClient';
import { rdwLookup, useCreateCar } from './fleet.api';

/** Add-a-car form, rendered inside the fleet "Auto toevoegen" modal. */
export function AddCarForm({
  onAdded,
  onCancel,
}: {
  /** Gets the car as it was stored, so the caller can go straight to its page. */
  onAdded?: (car: Car) => void;
  onCancel?: () => void;
}) {
  const [plate, setPlate] = useState('');
  const [eigendom, setEigendom] = useState<'EIGEN' | 'LEASE' | 'LEEN'>('EIGEN');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [found, setFound] = useState<RdwLookup | null>(null);
  const create = useCreateCar();

  async function onRdw() {
    if (!plate.trim()) return;
    setBusy(true);
    setError('');
    setFound(null);
    try {
      setFound(await rdwLookup(plate));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'RDW niet bereikbaar');
    } finally {
      setBusy(false);
    }
  }

  async function onAdd() {
    if (!found) return;
    setError('');
    try {
      const car = await create.mutateAsync({
        kenteken: found.kenteken,
        merk: found.merk,
        model: found.model,
        brandstof: found.brandstof,
        apkVervaldatum: found.apkVervaldatum,
        catalogusprijs: found.catalogusprijs,
        eigendom,
      });
      onAdded?.(car);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kon auto niet toevoegen');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Input
            label="Kenteken"
            placeholder="bv. AB123C"
            value={plate}
            autoFocus
            onChange={(e) => {
              setPlate(e.target.value);
              setFound(null);
              setError('');
            }}
          />
        </div>
        <Button variant="secondary" type="button" onClick={onRdw} disabled={busy}>
          {busy ? 'Ophalen…' : 'Ophalen bij RDW'}
        </Button>
      </div>

      <Dropdown
        label="Eigendom"
        block
        value={eigendom}
        onChange={(v) => setEigendom(v as 'EIGEN' | 'LEASE' | 'LEEN')}
        options={[
          { value: 'EIGEN', label: 'Eigen bezit' },
          { value: 'LEASE', label: 'Lease' },
          { value: 'LEEN', label: 'Leenauto' },
        ]}
      />

      {found && (
        <div
          style={{
            background: '#F4FAF7',
            border: '1px solid #BCD4DA',
            borderRadius: 4,
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 700,
            color: '#111111',
          }}
        >
          {found.merk} {found.model} · {found.brandstof} · {euro(found.catalogusprijs)} · APK{' '}
          {fmtDate(found.apkVervaldatum)}
        </div>
      )}

      {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}

      <span style={{ fontSize: 12, color: '#888888' }}>
        Merk, model, brandstof, catalogusprijs en APK-datum worden live opgehaald bij de RDW —
        alleen het kenteken is genoeg. Daarna sta je op de pagina van de auto, met de checklist van
        wat er nog moet gebeuren: verzekering, tankpas, bestuurder.
      </span>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <Button variant="ghost" type="button" onClick={() => onCancel?.()}>
          Annuleren
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onAdd}
          disabled={!found || create.isPending}
        >
          {create.isPending ? 'Bezig…' : 'Toevoegen'}
        </Button>
      </div>
    </div>
  );
}
